import { AuthFlow } from '~/auth-flow';
import { PristineValidation } from '~/globals/form-validation/form-validation';
import { bindContactInfoErrorOnValidation } from '~/marketplace/contact-info-detection-helpers';
import { InternalTracking } from '~/shared/modules/internal-tracking';
import { expandElement, collapseElement } from '~/shared/utils/expand-collapse';
import isFeatureGateEnabled from '~/shared/utils/feature-gates';
import { DrbConfirm } from '~/web-components/drb-confirm/drb-confirm-helpers';
import { trackGTMEvent } from '~/shared/utils/tracking/google-tag-manager-utils';
import { DrbDialog } from '~/web-components/drb-dialog/drb-dialog-helpers';
import { formatNumber, stringToNumber } from '~/shared/utils/pricing';
import messageIcon from '~/assets/icons/v2-confirm-message.svg?raw';
import { MessageDialogAi } from './message-dialog-ai';

// eslint-disable-next-line import/no-unresolved
import checkIcon from '~/assets/icons/check-verified.svg?raw';
// eslint-disable-next-line import/no-unresolved
import alertIcon from '~/assets/icons/v2-alert-pink.svg?raw';

export const MessageDialog = {
  init() {
    this.bindListeners();

    // Open the message dialog and restore draft (if available)
    this.openAndRestoreDraft();
  },

  getClosestMessageDialog(target) {
    return target.closest('drb-dialog#message-dialog');
  },

  // global listeners for message dialog related actions
  bindListeners() {
    this.bindMessageInputSync();
    this.bindTabNavigation();
    this.bindFormValidation();
    this.bindAuthFlow();
    this.bindTrackingListeners();
    this.initAiInputs();
  },

  initAiInputs() {
    document.addEventListener('drb-dialog-content-updated', (e) => {
      const messageDialog = e.target.closest('drb-dialog#message-dialog');
      if (!messageDialog) return;

      new MessageDialogAi(messageDialog, this);
    });
  },

  bindTabNavigation() {
    document.addEventListener('click', (e) => {
      const targetTab = e.target.closest('[data-message-dialog-tab]')?.getAttribute('data-message-dialog-tab');
      if (!targetTab) return;

      this.setActiveTab(targetTab);
    });
  },

  setActiveTab(tabName) {
    document.querySelectorAll('drb-dialog#message-dialog [data-message-dialog-tab]').forEach((tab) => {
      tab.classList.toggle('active', tabName === tab.getAttribute('data-message-dialog-tab'));
    });

    document.querySelector('drb-dialog#message-dialog [data-active-tab]')?.setAttribute('data-active-tab', tabName);
  },

  bindMessageInputSync() {
    document.addEventListener('input', (e) => {
      const messageDialog = this.getClosestMessageDialog(e.target);
      if (!messageDialog) return;

      const messageInputs = messageDialog.querySelectorAll('#message-dialog-message, #message-dialog-details');
      if (!Array.from(messageInputs).includes(e.target)) return;

      messageInputs.forEach((input) => {
        input.value = e.target.value;
      });

      // Update auth flow message preview
      const authPreview = messageDialog.querySelector('[data-message-auth-preview]');
      if (!authPreview) return;

      authPreview.textContent = e.target.value.slice(0, 150);
    });
  },

  bindFormValidation() {
    // bind form validation to message dialog forms when they are loaded
    document.addEventListener('drb-dialog-content-updated', (e) => {
      const messageDialog = this.getClosestMessageDialog(e.target);
      if (!messageDialog) return;

      messageDialog.querySelectorAll('[data-message-dialog-form]').forEach((form) => {
        form.pristineValidation = PristineValidation(form);
        this.bindBudgetFieldLogic(form);
        bindContactInfoErrorOnValidation(form);
      });

      // clear draft message when the message dialog is opened
      this.clearDraft();
    });

    // validate the form on submit
    document.addEventListener('submit', (e) => {
      const form = e.target.closest('[data-message-dialog-form]');
      const isLoggedIn = window.Dribbble?.JsConfig?.user?.isLoggedIn;
      if (!form) return;

      e.preventDefault();

      // submit the form if valid and not already submitting
      if (!form.hasAttribute('is-submitting') && form.pristineValidation?.validate()) {
        if (isFeatureGateEnabled('loggedOutMessageFlow') && !isLoggedIn) {
          this.saveDraft();
          this.setActiveTab('auth');
        } else {
          this.submitForm(form);
        }
      } else {
        this.clearDraftSendingMessageState();
        const firstError = form?.querySelector('.has-validation-error');
        firstError?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
      }
    });
  },

  bindBudgetFieldLogic(form) {
    const budgetField = form.querySelector('[data-project-budget]');
    const budgetNotSureCheckbox = form.querySelector('[data-project-budget-unsure]');
    if (!budgetField || !budgetNotSureCheckbox) return;

    // convert budget to a numeric format when user is typing
    budgetField.addEventListener('input', () => {
      budgetField.value = formatNumber(budgetField.value, { maximumFractionDigits: 0 });
    });

    // custom validator to check if budget is below the min cost
    const minCost = Number(budgetField.getAttribute('data-project-budget-min-cost'));
    form.pristineValidation?.addValidator(budgetField, (value) => {
      if (budgetNotSureCheckbox.checked) return true;

      const budgetCost = stringToNumber(value) || 0;

      return budgetCost >= minCost;
    }, `Budget must be at least $${minCost}`, 1, true);

    // custom validator that makes budget required when 'not sure' checkbox is unchecked
    form.pristineValidation?.addValidator(budgetField, (value) => {
      if (budgetNotSureCheckbox.checked) return true;
      return !!value?.length;
    }, 'Please enter your budget.', 2, true);

    // toggle budget field state based on 'not sure' checkbox
    budgetNotSureCheckbox.addEventListener('change', () => {
      budgetField.toggleAttribute('disabled', budgetNotSureCheckbox.checked);
      budgetField.value = '';

      if (budgetNotSureCheckbox.checked) {
        form.pristineValidation?.validate(budgetField);
      }

      // reset validation state for budget field to prevent immediate errors when typing
      if (budgetField.pristine) {
        budgetField.pristine.hasValidated = false;
      }
    });
  },

  bindTrackingListeners() {
    document.addEventListener('drb-dialog-content-updated', (e) => {
      const messageDialog = this.getClosestMessageDialog(e.target);
      if (!messageDialog) return;

      const form = messageDialog.querySelector('[data-message-dialog-form]');
      const userId = form?.querySelector("input[name='engagement[recipient_id]']")?.value;
      if (!userId) return;

      InternalTracking.trackSearchMessageModalDisplayed(userId, messageDialog);

      trackGTMEvent('MessageModalDisplayed', {
        userId,
      });
    });
  },

  bindAuthFlow() {
    document.addEventListener('drb-dialog-content-updated', (e) => {
      const messageDialog = this.getClosestMessageDialog(e.target);
      if (!messageDialog) return;

      // Init initial auth screens inside the message dialog
      const authScreens = messageDialog.querySelectorAll('[data-message-auth-container] [data-screen]');
      authScreens.forEach((screen) => AuthFlow.initScreen(screen));
    });

    // Update DOM of the profile auth screen when it is injected.
    // (Ideally we can do this in the Rails partial, but we currently don't
    // have a clear path of dictating which auth flow the user is in. Can go with
    // updating this screens DOM via JS until we have a stronger need otherwise)
    document.addEventListener('auth-screen-injected', (e) => {
      const messageDialog = this.getClosestMessageDialog(e.target);
      const screen = e.target;
      const screenId = screen?.getAttribute('data-screen');
      const form = screen?.querySelector('form[data-auth-form]');
      const skipProfileBtn = form?.querySelector('[data-skip-profile-button]');
      const submitBtn = form?.querySelector('[data-submit]');
      if (!messageDialog || screenId !== 'auth-profile' || !form) return;

      // Mark the profile screen as the end of the auth flow
      const endOfFlowInput = document.createElement('input');
      endOfFlowInput.type = 'hidden';
      endOfFlowInput.name = 'end_of_flow';
      endOfFlowInput.value = 'true';

      // Assume the user has the `hire` intent
      const intentionsInput = document.createElement('input');
      intentionsInput.type = 'hidden';
      intentionsInput.name = 'intentions';
      intentionsInput.value = 'hire';

      // Inject hidden fields
      form.appendChild(endOfFlowInput);
      form.appendChild(intentionsInput);

      // Allow the user to skip profile creation and send the message
      if (skipProfileBtn) {
        skipProfileBtn.addEventListener('click', (ev) => {
          ev.preventDefault();
          form.requestSubmit();
        });

        skipProfileBtn.removeAttribute('data-screen-transition');
        skipProfileBtn.textContent = 'Skip and Send Message';
      }

      // Update submit button text
      if (submitBtn) submitBtn.textContent = 'Send Message';
    });
  },

  async submitForm(form) {
    const defaultServerErrorMsg = 'Failed to send message';
    const submitBtn = form.querySelector('[data-message-dialog-submit]');
    const formError = form.querySelector('[data-form-error]');

    // disable the submit button and show loading state
    const toggleFormSubmitting = (isSubmitting) => {
      form.toggleAttribute('is-submitting', isSubmitting);
      submitBtn.disabled = isSubmitting;
      submitBtn.classList.toggle('btn2--loading', isSubmitting);
    };

    // incase of server error, show error message
    const setServerErrorMsg = (errorMsg) => {
      if (!formError) return;
      formError.toggleAttribute('hidden', !errorMsg);
      formError.innerText = errorMsg;
    };

    // reset state
    toggleFormSubmitting(true);
    setServerErrorMsg(null);

    try {
      const response = await fetch(form.getAttribute('action'), {
        method: 'POST',
        body: this.getTransformedFormData(form),
        headers: {
          'X-Requested-With': 'XMLHttpRequest',
        },
      });

      const responseText = await response.text();

      if (!response.ok) {
        // use error message provided by BE if available (422), otherwise use default
        const errorMsg = response.status === 422 ? responseText : defaultServerErrorMsg;
        throw new Error(errorMsg || defaultServerErrorMsg);
      }

      // track `UserMessaged` event
      const recipientId = form.querySelector('[data-recipient-id-input]')?.value;
      if (recipientId) InternalTracking.trackSearchUserMessaged(recipientId);

      const dialog = form.closest('drb-dialog');

      // If success response includes suggested designers, show designers in the dialog and hide form content
      if (isFeatureGateEnabled('suggestedDesigners') && responseText?.includes('data-suggested-designers')) {
        this.showSuggestedDesigners(dialog, responseText);
      } else {
        // otherwise update the dialog content with the response (e.g. success message, etc.)
        dialog?.setContent(responseText);
      }

      const messageID = dialog.querySelector('[data-message-id]')?.getAttribute('data-message-id') || null;

      trackGTMEvent('UserMessaged', {
        recipientId,
        messageID,
      });
    } catch (error) {
      const errorMsg = error instanceof Error ? error.message : defaultServerErrorMsg;
      setServerErrorMsg(errorMsg);
      console.error(errorMsg, error);
    } finally {
      this.clearDraftSendingMessageState();
      toggleFormSubmitting(false);
    }
  },

  getTransformedFormData(form) {
    const formData = new FormData(form);

    // convert back budget from numeric (with commas) to integer
    if (formData.has('budget_dollars')) {
      formData.set('budget_dollars', stringToNumber(formData.get('budget_dollars')));
    }

    switch (form.getAttribute('data-message-dialog-form')) {
      case 'workRequest':
        formData.set('engagement[budget_cents]', parseInt(formData.get('budget_dollars') || 0, 10) * 100);
        formData.delete('budget_dollars');
        break;

      default:
        break;
    }

    return formData;
  },

  showSuggestedDesigners(dialog, responseText) {
    const messageSuccessContainer = dialog?.querySelector('[data-message-success-container]');
    const formContent = dialog?.querySelector('[data-message-dialog-form-content]');
    if (!formContent || !messageSuccessContainer) return;

    // Inject suggested designers, then show container
    messageSuccessContainer.innerHTML = responseText;
    messageSuccessContainer.toggleAttribute('hidden', false);

    // Hide form content so it can be expanded/collapsed
    collapseElement(formContent);
    formContent.setAttribute('inert', ''); // Prevent focusing on the form content when collapsed

    // Set up listeners
    this.bindToggleFormBtn(dialog);
    this.bindViewSentMessageBtn(dialog);
    this.rebindFormValidation(dialog);
    this.bindSendBulkRequest(dialog);
  },

  bindToggleFormBtn(dialog) {
    const button = dialog?.querySelector('[data-toggle-form-btn]');
    const requestForm = dialog?.querySelector('[data-message-dialog-form-content]');

    if (!dialog || !button || !requestForm) return;

    button.addEventListener('click', () => {
      if (requestForm.hasAttribute('data-collapsed')) {
        expandElement(requestForm);
        button.textContent = 'Hide Message';
        requestForm.removeAttribute('inert');
      } else {
        collapseElement(requestForm);
        button.textContent = 'Edit Message';
        requestForm.setAttribute('inert', '');
      }
    });
  },

  bindViewSentMessageBtn(dialog) {
    const button = dialog?.querySelector('[data-view-sent-message-btn]');
    const path = dialog?.querySelector('[data-thread-path]')?.getAttribute('data-thread-path') || '';
    if (!button || !path) return;

    button.addEventListener('click', () => {
      window.location = path;
    });
  },

  bindSendBulkRequest(dialog) {
    const form = dialog.querySelector('[data-message-dialog-form="workRequest"]');
    const button = dialog?.querySelector('[data-send-bulk-request]');

    if (!button || !dialog || !form) return;

    button.addEventListener('click', () => {
      if (!form.hasAttribute('is-submitting') && form.pristineValidation?.validate()) {
        this.sendBulkRequest(dialog);
      }
    });
  },

  async sendBulkRequest(dialog) {
    const form = dialog.querySelector('[data-message-dialog-form="workRequest"]');
    const sendButton = dialog.querySelector('[data-send-bulk-request]');
    const url = sendButton?.getAttribute('data-send-bulk-request');

    if (!form || !sendButton || !url) return;

    // Remove any existing recipient ID and replace with array of suggested designer ids
    const bulkRecipients = Array.from(form.querySelectorAll('[data-suggested-designer]:checked')).map((designer) => designer.value);
    const formData = this.getTransformedFormData(form);
    const messageID = dialog.querySelector('[data-message-id]')?.getAttribute('data-message-id') || null;

    formData.delete('engagement[recipient_id]');

    bulkRecipients.forEach((id) => {
      formData.append('engagement[recipient_id][]', id);
    });

    // Add original message_id for BE to handle bulk requests correctly
    formData.set('engagement[message_id]', messageID);

    const toggleFormSubmitting = (isSubmitting) => {
      form.toggleAttribute('is-submitting', isSubmitting);
      sendButton.disabled = isSubmitting;
      sendButton.classList.toggle('btn2--loading', isSubmitting);
    };

    try {
      toggleFormSubmitting(true);

      const response = await fetch(url, {
        method: 'POST',
        body: formData,
        headers: {
          'X-Requested-With': 'XMLHttpRequest',
        },
      });

      if (!response.ok) {
        throw new Error('Something went wrong. Please try again later.');
      }

      dialog.close();

      // Fire off internal and GTM tracking for suggested designers
      bulkRecipients.forEach((id) => {
        InternalTracking.trackSearchUserMessaged(id);
        trackGTMEvent('UserMessaged', {
          recipientId: id,
          parentMessageID: messageID,
        });
      });

      DrbConfirm.open({
        title: 'Your messages have been sent!',
        iconHtml: checkIcon,
        centered: true,
        cancelText: 'Close',
        confirmText: '',
      });
    } catch (error) {
      toggleFormSubmitting(false);

      DrbConfirm.open({
        title: error,
        iconHtml: alertIcon,
        centered: true,
        cancelText: '',
        confirmText: 'Close',
      });
    }
  },

  rebindFormValidation(dialog) {
    const form = dialog.querySelector('[data-message-dialog-form="workRequest"]');
    if (!form) return;

    // Rebind Pristine validation to include newly added inputs
    if (form.pristineValidation) form.pristineValidation.destroy();

    form.pristineValidation = PristineValidation(form);
  },

  open(recipientId, remoteDialogOptions = {}) {
    if (!recipientId) return null;

    return DrbDialog.openRemote({
      url: `/messages/new?recipient_id=${recipientId}`,
      dialogId: 'message-dialog',
      nonModalMode: true,
      ...remoteDialogOptions,
    });
  },

  saveDraft() {
    const messageDialog = document.querySelector('drb-dialog#message-dialog');
    const isWorkRequest = !!messageDialog?.querySelector('[data-message-dialog-toggle]')?.checked;
    const recipientId = messageDialog?.querySelector('[data-recipient-id-input]')?.value;
    if (!messageDialog || !recipientId) return;

    // Only store fields that are whitelisted, preventing unnecessary data
    // in the draft message, such as CSRF tokens or other metadata.
    const whitelistFields = [
      'message[body]',
      'engagement[project_details]',
      'engagement[target_date]',
      'budget_dollars',
    ];

    const messageDraft = {
      isWorkRequest,
      recipientId,
      fields: {},
    };

    whitelistFields.forEach((field) => {
      const input = messageDialog.querySelector(`form[data-message-dialog-form] [name="${field}"]`);
      if (input) messageDraft.fields[field] = input.value;
    });

    // Store the draft in localStorage
    localStorage.setItem('messageDraft', JSON.stringify(messageDraft));
  },

  openAndRestoreDraft() {
    // Only restore draft if the user is logged in
    const isLoggedIn = window.Dribbble?.JsConfig?.user?.isLoggedIn;
    if (!isLoggedIn) return;

    try {
      // Check localStorage for a saved draft
      const messageDraft = JSON.parse(localStorage.getItem('messageDraft'));
      if (!messageDraft) return;

      const { isWorkRequest, recipientId, fields } = messageDraft;

      // Show `Sending message...` UI when restoring draft (since we'll try to
      // send the message immediately). We can use the dialogs `remote loading slot`
      // for this, which allows us to show it immediately without waiting for the
      // dialog to load.
      const remoteLoadingSlotHtml = `
        <div class="message-dialog-sending">
          ${messageIcon}
          <h3 class="type-title-s">Sending message...</h3>
        </div>
      `;

      // Open the message dialog
      const messageDialog = this.open(recipientId, {
        remoteLoadingSlotHtml,
        remoteInitialHeight: 'fit-content',
        shouldResetIsFetching: false,
      });

      if (!messageDialog) return;

      // Restore draft fields once dialog content is ready
      messageDialog.addEventListener('drb-dialog-content-updated', () => {
        // Wait for next frame so web components can mount (e.g. `drb-dropdown`)
        setTimeout(() => {
          // Set the work request toggle checked state
          const workRequestToggle = messageDialog.querySelector('[data-message-dialog-toggle]');
          if (workRequestToggle) workRequestToggle.checked = isWorkRequest;

          // Restore the whitelisted field values
          Object.entries(fields).forEach(([name, value]) => {
            const input = messageDialog.querySelector(`form[data-message-dialog-form] [name="${name}"]`);
            if (input) input.value = value;
          });

          setTimeout(() => {
            // Find the target form we want to submit based on the work request
            // state (work request or normal message)
            const form = messageDialog.querySelector(`[data-message-dialog-form="${isWorkRequest ? 'workRequest' : 'message'}"]`);

            // Automatically submit the form once it's been restored
            form?.requestSubmit();
          }, 0);
        }, 0);
      }, { once: true });
    } catch (error) {
      console.error('Failed to restore message draft:', error);
    }
  },

  // Hide the "Message sending..." loading state and show the real dialog content
  clearDraftSendingMessageState() {
    const messageDialog = document.querySelector('drb-dialog#message-dialog');
    if (!messageDialog) return;

    messageDialog.isFetchingRemote = false;
  },

  clearDraft() {
    // Clear the draft from localStorage
    localStorage.removeItem('messageDraft');
  },
};
